import { useCallback, useEffect, useState } from "react";
import { Lock, LogIn, LogOut, Rabbit, Radio, User } from "lucide-react";
import PublicSiteLayout, { PageHero, PublicCard, PublicLinkButton } from "../components/PublicSiteLayout";
import { api, buildKickLoginUrl, buildLoginUrl } from "../lib/api";
import { getViewerSession } from "../lib/viewerSession";

const SHOW_MOCK_AUTH =
  process.env.NODE_ENV === "development" ||
  process.env.REACT_APP_ENABLE_MOCK_AUTH === "true";

const ROLE_OPTIONS = ["viewer", "broadcaster", "admin", "dev"];

const normalizeLogin = (displayName, twitchUserId) => {
  const fromName = displayName.trim().toLowerCase().replace(/[^a-z0-9_]/g, "");
  const fromId = twitchUserId.trim().toLowerCase().replace(/[^a-z0-9_]/g, "");
  return fromName || fromId || "localviewer";
};

const loginReturnTo = (fallback = "/profile") => {
  if (typeof window === "undefined") return fallback;
  const value = new URLSearchParams(window.location.search).get("return_to");
  return value && value.startsWith("/") ? value : fallback;
};

function ViewerLoginButtons({ returnTo = "/profile" }) {
  const twitchLoginUrl = buildLoginUrl("viewer", returnTo);
  const kickLoginUrl = buildKickLoginUrl("viewer", returnTo);
  return (
    <div className="flex flex-wrap gap-3">
      <a
        href={twitchLoginUrl}
        target="_top"
        rel="noopener"
        className="inline-flex items-center gap-2 px-5 py-3 rounded bg-[#9146ff] text-white font-medium hover:brightness-110 transition"
        data-testid="twitch-login-button"
      >
        <Rabbit className="w-4 h-4" /> Continue with Twitch
      </a>
      <a
        href={kickLoginUrl}
        target="_top"
        rel="noopener"
        className="inline-flex items-center gap-2 px-5 py-3 rounded border border-[#53fc18]/70 bg-[#53fc18]/15 text-[#d8ffd1] font-medium hover:bg-[#53fc18]/25 transition"
        data-testid="kick-login-button"
      >
        <Radio className="w-4 h-4" /> Continue with Kick
      </a>
    </div>
  );
}

function MockAuthPanel() {
  const [form, setForm] = useState({
    twitch_user_id: "local_viewer_1",
    display_name: "Local Viewer",
    role: "viewer",
  });
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const loadSession = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get("/auth/me");
      setSession(data?.authenticated ? data : null);
    } catch (err) {
      setSession(null);
      setError("Could not check the current local session.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  const updateForm = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const submitLogin = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError("");

    try {
      await api.post("/auth/mock-login", {
        twitch_user_id: form.twitch_user_id.trim(),
        login: normalizeLogin(form.display_name, form.twitch_user_id),
        display_name: form.display_name.trim(),
        role: form.role,
      });
      await loadSession();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(detail || "Mock login failed. Check that MOCK_AUTH_ENABLED is true on the backend.");
    } finally {
      setSubmitting(false);
    }
  };

  const logout = async () => {
    setSubmitting(true);
    setError("");

    try {
      await api.post("/auth/logout");
    } catch (err) {
      // The backend may redirect after clearing cookies; local UI still clears its visible session.
    } finally {
      setSession(null);
      setSubmitting(false);
    }
  };

  return (
    <section className="mt-8 surface p-6 bg-black/75">
      <div className="flex items-start gap-3">
        <User className="w-5 h-5 text-[hsl(var(--currency-event))] mt-0.5" strokeWidth={1.5} />
        <div>
          <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Local development auth</div>
          <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
            Development mock login only. Use the Twitch or Kick buttons above for the deployed OAuth flow.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="mt-5 text-sm text-[hsl(var(--muted-foreground))]">Checking local session...</div>
      ) : session ? (
        <div className="mt-6 space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 text-sm">
            <div className="rounded border border-[hsl(var(--border))] p-3">
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Twitch User ID</div>
              <div className="mt-1 font-mono text-[hsl(var(--foreground))] break-all">{session.twitch_user_id}</div>
            </div>
            <div className="rounded border border-[hsl(var(--border))] p-3">
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Display Name</div>
              <div className="mt-1 text-[hsl(var(--foreground))] break-words">{session.display_name || session.login}</div>
            </div>
            <div className="rounded border border-[hsl(var(--border))] p-3">
              <div className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Role</div>
              <div className="mt-1 text-[hsl(var(--foreground))] capitalize">{session.role || "viewer"}</div>
            </div>
          </div>

          <button
            type="button"
            onClick={logout}
            disabled={submitting}
            className="inline-flex items-center gap-2 px-4 py-2 rounded border border-[hsl(var(--border))] text-sm text-[hsl(var(--muted-foreground))] hover:text-white hover:border-[hsl(var(--currency-event))] disabled:opacity-50 transition"
          >
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      ) : (
        <form onSubmit={submitLogin} className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Twitch User ID</span>
            <input
              value={form.twitch_user_id}
              onChange={(event) => updateForm("twitch_user_id", event.target.value)}
              required
              className="mt-2 w-full rounded border border-[hsl(var(--border))] bg-black/60 px-3 py-2 text-sm text-[hsl(var(--foreground))] outline-none focus:border-[hsl(var(--currency-event))]"
            />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Display Name</span>
            <input
              value={form.display_name}
              onChange={(event) => updateForm("display_name", event.target.value)}
              required
              className="mt-2 w-full rounded border border-[hsl(var(--border))] bg-black/60 px-3 py-2 text-sm text-[hsl(var(--foreground))] outline-none focus:border-[hsl(var(--currency-event))]"
            />
          </label>

          <label className="block">
            <span className="text-xs uppercase tracking-widest text-[hsl(var(--muted-foreground))]">Role</span>
            <select
              value={form.role}
              onChange={(event) => updateForm("role", event.target.value)}
              className="mt-2 w-full rounded border border-[hsl(var(--border))] bg-black/60 px-3 py-2 text-sm text-[hsl(var(--foreground))] outline-none focus:border-[hsl(var(--currency-event))]"
            >
              {ROLE_OPTIONS.map((role) => (
                <option key={role} value={role}>{role}</option>
              ))}
            </select>
          </label>

          <div className="flex items-end">
            <button
              type="submit"
              disabled={submitting}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-5 py-2.5 rounded bg-[hsl(var(--currency-event))] text-sm text-white font-medium hover:brightness-110 disabled:opacity-50 transition"
            >
              <LogIn className="w-4 h-4" /> {submitting ? "Logging in..." : "Login"}
            </button>
          </div>
        </form>
      )}

      {error && (
        <div className="mt-4 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">
          {error}
        </div>
      )}
    </section>
  );
}

function AccountStatusPanel({ returnTo = "/profile" }) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadSession = useCallback(async () => {
    setLoading(true);
    setError("");
    const { session, error } = await getViewerSession();
    setSession(session);
    if (error) {
      setError("The account service could not be reached. The viewer login preview is unavailable right now.");
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    loadSession();
  }, [loadSession]);

  return (
    <section className="mt-8 surface p-6 bg-black/75">
      <div className="flex items-start gap-3">
        <Lock className="w-5 h-5 text-[hsl(var(--currency-event))] mt-0.5" strokeWidth={1.5} />
        <div>
          <div className="label-eyebrow mb-2 text-[hsl(var(--currency-event))]">Account status</div>
          <h2 className="text-2xl font-semibold tracking-tight text-[hsl(var(--foreground))]">Sign in with Twitch or Kick.</h2>
        </div>
      </div>

      {loading ? (
        <p className="mt-4 text-sm text-[hsl(var(--muted-foreground))]">Checking account service...</p>
      ) : error ? (
        <div className="mt-5 rounded border border-red-500/40 bg-red-500/10 px-3 py-2 text-sm text-red-100">{error}</div>
      ) : session ? (
        <div className="mt-5 rounded border border-[hsl(var(--currency-event))]/50 bg-[hsl(var(--currency-event))]/10 px-3 py-2 text-sm text-[hsl(var(--muted-foreground))]">
          A session is active for <span className="text-[hsl(var(--foreground))]">{session.display_name || session.login || session.twitch_user_id}</span>.
          <div className="mt-4 flex flex-wrap gap-3">
            <PublicLinkButton href="/profile" primary><Rabbit className="w-4 h-4" /> Open profile</PublicLinkButton>
          </div>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
            No viewer session is active. Pick the platform you are watching from and the site will return you to the right viewer page.
          </p>
          <ViewerLoginButtons returnTo={returnTo} />
        </div>
      )}
    </section>
  );
}

export default function Login() {
  const returnTo = loginReturnTo("/profile");
  return (
    <PublicSiteLayout>
      <PageHero
        eyebrow="Account access"
        title="Connect your viewer account."
        body="Sign in with Twitch or Kick so profiles, redeems, balances, events, and rewards all point back to one community identity."
        actions={<ViewerLoginButtons returnTo={returnTo} />}
      />

      <section className="mt-12 grid grid-cols-1 sm:grid-cols-2 gap-4">
        <PublicCard icon={Lock} title="Platform-flexible access" body="Use the platform you are watching from and keep the same viewer-facing profile experience." />
        <PublicCard icon={Rabbit} title="One community profile" body="Linked Twitch and Kick accounts share redeems, rewards, balances, identity preferences, and progression." />
      </section>

      <AccountStatusPanel returnTo={returnTo} />

      {SHOW_MOCK_AUTH && <MockAuthPanel />}
    </PublicSiteLayout>
  );
}
